import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import pkg from '../../../package.json';
import { Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import AppBanner from '../components/AppBanner';
import AppDownloadPopup from '../components/AppDownloadPopup';
import TopBar from '../components/TopBar';
import { useBanner } from '../hooks/useBanner';
import styled, { ThemeProvider } from 'styled-components';
import { Home, Receipt, Settings, BarChart3, Menu, X, Building2, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { saveKoffieSession } from '../../koffiereparatie-geleen/KoffieApp';
import { usePermissions } from '../hooks/usePermissions';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../../styles/theme';

// Import screens

import DashboardScreen from '../screens/DashboardScreen';
import StatsScreen from '../screens/StatsScreen';
import ChartsScreen from '../screens/ChartsScreen';
import ProductAnalyticsScreen from '../screens/ProductAnalyticsScreen';
import WebsiteAnalyticsScreen from '../screens/WebsiteAnalyticsScreen';
import OrdersScreen from '../screens/OrdersScreen';
import SettingsScreen from '../screens/SettingsScreen';
import AccountSettingsScreen from '../screens/AccountSettingsScreen';
import LayoutSettingsScreen from '../screens/LayoutSettingsScreen';
import ProductStatusScreen from '../screens/ProductStatusScreen';
import ProductDetailPage from '../screens/ProductDetailPage';
import OpeningHoursScreen from '../screens/OpeningHoursScreen';
import NotificationsScreen from '../screens/NotificationsScreen';
import LegalScreen from '../screens/LegalScreen';
import UsersScreen from '../screens/UsersScreen';
import BannerScreen from '../screens/BannerScreen';
import PinSettingsScreen from '../screens/PinSettingsScreen';

const { version } = pkg;

// Context voor menu state
const MenuContext = createContext();

export const useMenu = () => useContext(MenuContext);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background-color: ${props => props.theme.colors.background};
  position: relative;
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile browsers */
    overflow: hidden;
    touch-action: pan-x pan-y;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 0;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const SideNav = styled.nav`
  width: ${p => p.$collapsed ? '64px' : '220px'};
  min-width: ${p => p.$collapsed ? '64px' : '220px'};
  height: 100%;
  background: ${props => props.theme.colors.surface};
  border-right: none;
  display: flex;
  flex-direction: column;
  padding: 8px 0 32px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0;
  position: relative;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  z-index: 10;
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    width: 64px;
    min-width: 64px;
  }
`;

const CollapseBtn = styled.button`
  position: absolute;
  bottom: 80px;
  left: ${p => p.$collapsed ? '52px' : '208px'};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${p => p.theme.colors.muted};
  transition: left 0.22s cubic-bezier(0.4, 0, 0.2, 1), background 0.15s, color 0.15s;
  z-index: 20;
  flex-shrink: 0;
  &:hover {
    background: ${p => p.theme.colors.border};
    color: ${p => p.theme.colors.text};
  }
  @media (max-width: 768px) { display: none; }
`;

const SideNavLogo = styled.div`
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 20px 12px 20px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const SideNavLogoImg = styled.img`
  width: 140px;
  height: auto;
  filter: ${props => props.theme.mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)'};
  opacity: 0.9;
`;

const NavSection = styled.div`
  padding: 0 16px;
  margin-bottom: 8px;
`;

const NavSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.muted};
  opacity: 0.5;
  padding: 0 12px;
  margin-bottom: 4px;
  margin-top: 20px;
  white-space: nowrap;
  overflow: hidden;
  display: ${p => p.$collapsed ? 'none' : 'block'};
`;

const NavDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.colors.border};
  margin: 12px 16px;
`;

const SideNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  color: ${props => props.$active ? '#fff' : props.theme.colors.muted};
  text-decoration: none;
  transition: all 0.15s ease;
  border-radius: 10px;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 14px;
  letter-spacing: -0.01em;
  background: ${props => props.$active ? props.theme.colors.brand : 'transparent'};

  &:hover {
    color: ${props => props.$active ? '#fff' : props.theme.colors.text};
    background: ${props => props.$active ? props.theme.colors.brand : (props.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')};
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: ${props => props.$active ? '1' : '0.5'};
    flex-shrink: 0;
  }
`;

const NavItemLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  display: ${p => p.$collapsed ? 'none' : 'block'};
`;

const NavShortcut = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'};
  border: 1px solid ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.18)'};
  border-radius: 5px;
  padding: 2px 7px;
  font-family: monospace;
  letter-spacing: 0;
`;

const StripeDashboardButton = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.muted};
  text-decoration: none;
  transition: all 0.18s ease;
  border-radius: 10px;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.01em;
  width: 100%;
  box-sizing: border-box;
  text-align: left;

  &:hover {
    color: ${props => props.theme.colors.text};
    background: ${props => props.theme.colors.border};
  }

  svg { width: 18px; height: 18px; flex-shrink: 0; opacity: 0.55; }

  .stripe-text { flex: 1; font-weight: 500; color: inherit; }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 999;
  opacity: ${props => props.$isOpen ? '1' : '0'};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  
  /* Performance boost */
  will-change: opacity, visibility;
  contain: layout style paint;
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    background: rgba(0, 0, 0, 0.5);
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ConfirmModal = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 18px;
  max-width: 380px;
  width: 90%;
  padding: 24px 20px 20px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.4);
  border: 1px solid ${props => props.theme.colors.border};
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const ConfirmText = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: ${props => props.theme.colors.muted};
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ConfirmBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 9px 20px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  background: ${props => props.$primary ? '#f97316' : props.theme.colors.border};
  color: ${props => props.$primary ? '#fff' : props.theme.colors.text};
  transition: filter 0.15s ease;
  &:hover { filter: brightness(0.92); }
`;

const CompanyItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  background: ${props => props.$active
    ? props.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
    : 'transparent'};
  border: 1px solid ${props => props.$active
    ? props.theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
    : 'transparent'};
  border-radius: 10px;
  cursor: ${props => props.$active ? 'default' : 'pointer'};
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '400'};
  color: ${props => props.$active ? props.theme.colors.text : props.theme.colors.muted};
  text-align: left;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  &:hover:not(:disabled):not([data-active="true"]) {
    background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'};
    color: ${props => props.theme.colors.text};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const CompanyIcon = styled.div`
  width: 26px; height: 26px; border-radius: 7px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
`;

const CompanyLogo = styled.img`
  width: ${p => p.$size || '16px'};
  height: ${p => p.$size || '16px'};
  object-fit: contain;
  filter: ${p => p.theme.mode === 'light' ? 'brightness(0)' : 'brightness(0) invert(1)'};
`;

const CompanyActiveDot = styled.div`
  width: 5px; height: 5px; border-radius: 50%;
  background: #22c55e; margin-left: auto; flex-shrink: 0;
`;

const SideNavAppInfo = styled.div`
  margin: auto 16px 0;
  padding: 12px 14px;
  border-top: 1px solid ${p => p.theme.colors.border};
  opacity: ${p => p.$collapsed ? 0 : 1};
  overflow: hidden;
  transition: opacity 0.15s ease;
`;

const SideNavAppName = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  letter-spacing: 0.01em;
`;

const SideNavAppMeta = styled.div`
  font-size: 10px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
  line-height: 1.5;
`;

const SWITCH_COMPANIES = [
  { slug: 'kmfc-shop', name: 'KMFC Shop', logo: 'https://kmfcshoponline.nl/assets/logo.png', logoSize: '16px' },
  { slug: 'koffiereparatie-geleen', name: 'Koffiereparatie Geleen', logo: '/assets/Koffie-geleen_logo.png', logoSize: '20px' },
];

function SettingsStackRoutes() {
  return (
    <Routes>
      <Route path="/" element={<SettingsScreen />} />
      <Route path="/account" element={<AccountSettingsScreen />} />
      <Route path="/layout" element={<LayoutSettingsScreen />} />
      <Route path="/product-status" element={<ProductStatusScreen />} />
      <Route path="/opening-hours" element={<OpeningHoursScreen />} />
      <Route path="/notifications" element={<NotificationsScreen />} />
      <Route path="/legal" element={<LegalScreen />} />
      <Route path="/users" element={<UsersScreen />} />
      <Route path="/banner" element={<BannerScreen />} />
      <Route path="/pin" element={<PinSettingsScreen />} />
    </Routes>
  );
}

export default function MainTabs() {
  const { banner, visible, dismiss } = useBanner('kmfc-shop');
  const { permissions, loading } = usePermissions();
  const { themeMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isTabChanging, setIsTabChanging] = useState(false);
  const [showStripeConfirm, setShowStripeConfirm] = useState(false);
  const [showWebsiteConfirm, setShowWebsiteConfirm] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('kmfc_sidebar_collapsed') === 'true');

  const toggleSidebar = () => setSidebarCollapsed(prev => {
    const next = !prev;
    localStorage.setItem('kmfc_sidebar_collapsed', next);
    return next;
  });
  const isSuperAdmin = (() => { try { return !!JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null'); } catch { return false; } })();

  const switchCompany = async (company) => {
    if (switching || company.slug === 'kmfc-shop') return;
    setSwitching(company.slug);
    menuContextValue.closeMenu();
    try {
      const sa = JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null');
      if (!sa) return;
      saveKoffieSession({
        id: sa.id, username: sa.username,
        company_slug: company.slug, company_name: company.name,
        role_id: 1,
        role: { slug: 'admin', can_view_new_orders: true, can_view_order_history: true, can_manage_settings: true, can_edit_product_data: true, can_change_product_status: true, can_view_product_statistics: true, can_view_revenue_statistics: true },
        access_token: sa.access_token, refresh_token: sa.refresh_token,
        expires_at: sa.expires_at, login_time: new Date().toISOString(),
      });
      window.location.href = '/#/Koffiereparatie/dashboard';
    } catch { setSwitching(null); }
  };

  // Menu context provider
  const menuContextValue = {
    isOpen,
    toggleMenu: () => {
      const newState = !isOpen;
      console.log('Menu toggle:', newState);
      setIsOpen(newState);
    },
    closeMenu: () => setIsOpen(false)
  };

  // Handle navigation with clean transition
  const handleNavigation = (callback) => {
    setIsTabChanging(true);
    setTimeout(() => {
      callback();
      setTimeout(() => {
        setIsTabChanging(false);
      }, 300);
    }, 100);
  };


  if (loading) {
    return null;
  }

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };


  return (
    <MenuContext.Provider value={menuContextValue}>
      <Container>
        {!location.pathname.match(/^\/app\/settings\/.+/) && (
          <AppBanner banner={banner} visible={visible} dismiss={dismiss} />
        )}
        <TopBar showShopStatus />
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>

        <SideNav $collapsed={sidebarCollapsed}>
          <NavSection>
            <NavSectionLabel $collapsed={sidebarCollapsed}>Navigatie</NavSectionLabel>

            <SideNavItem to="/KMFC/dashboard" $active={isActive('/KMFC/dashboard')} $collapsed={sidebarCollapsed} onClick={() => handleNavigation(() => {})}>
              <Home size={18} />
              <NavItemLabel $collapsed={sidebarCollapsed}>Dashboard</NavItemLabel>
            </SideNavItem>

            {(permissions.can_view_new_orders || permissions.can_view_order_history) && (
              <SideNavItem to="/KMFC/orders" $active={isActive('/KMFC/orders')} $collapsed={sidebarCollapsed} onClick={() => handleNavigation(() => {})}>
                <Receipt size={18} />
                <NavItemLabel $collapsed={sidebarCollapsed}>Bestellingen</NavItemLabel>
              </SideNavItem>
            )}

            <SideNavItem to="/KMFC/stats" $active={isActive('/KMFC/stats')} $collapsed={sidebarCollapsed} onClick={() => handleNavigation(() => {})}>
              <BarChart3 size={18} />
              <NavItemLabel $collapsed={sidebarCollapsed}>Statistieken</NavItemLabel>
            </SideNavItem>

            <SideNavItem to="/KMFC/settings" $active={isActive('/KMFC/settings')} $collapsed={sidebarCollapsed} onClick={() => handleNavigation(() => {})}>
              <Settings size={18} />
              <NavItemLabel $collapsed={sidebarCollapsed}>Instellingen</NavItemLabel>
            </SideNavItem>
          </NavSection>


          <NavDivider style={{ margin: sidebarCollapsed ? '8px 12px' : '16px 16px' }} />

          <NavSection>
            <NavSectionLabel $collapsed={sidebarCollapsed}>Extern</NavSectionLabel>
            <StripeDashboardButton as="button" onClick={() => setShowWebsiteConfirm(true)}>
              <ExternalLink size={18} />
              {!sidebarCollapsed && <span className="stripe-text">Website</span>}
            </StripeDashboardButton>
            <StripeDashboardButton as="button" onClick={() => setShowStripeConfirm(true)}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path d="M12 2L2 7V12C2 16.5 4.23 20.68 7.62 23.15L12 24L16.38 23.15C19.77 20.68 22 16.5 22 12V7L12 2Z" fill={themeMode === 'light' ? '#1a56db' : '#3b82f6'} />
              </svg>
              {!sidebarCollapsed && <span className="stripe-text">Stripe</span>}
            </StripeDashboardButton>
          </NavSection>

          <SideNavAppInfo $collapsed={sidebarCollapsed}>
            <SideNavAppName>KMFC Shop Dashboard</SideNavAppName>
            <SideNavAppMeta>Versie {version} · Webvonk</SideNavAppMeta>
            <SideNavAppMeta>© {new Date().getFullYear()} Webvonk</SideNavAppMeta>
          </SideNavAppInfo>

        </SideNav>

        <CollapseBtn $collapsed={sidebarCollapsed} onClick={toggleSidebar}>
          {sidebarCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </CollapseBtn>

        <MainContent>
          <Routes>
            <Route path="/dashboard" element={<DashboardScreen />} />
            <Route path="/stats" element={<StatsScreen />} />
            <Route path="/stats/charts" element={<ChartsScreen />} />
            <Route path="/stats/website" element={<WebsiteAnalyticsScreen />} />
            <Route path="/top-products" element={<ProductAnalyticsScreen />} />
            
            {(permissions.can_view_new_orders || permissions.can_view_order_history) && (
              <>
                <Route path="/orders" element={<OrdersScreen />} />
              </>
            )}
            
            <Route path="/settings/*" element={<SettingsStackRoutes />} />
<Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="*" element={<Navigate to="/KMFC/dashboard" replace />} />
          </Routes>
        </MainContent>
        </div>
      </Container>
      <AppDownloadPopup />
      {showStripeConfirm && (
        <ConfirmOverlay onClick={() => setShowStripeConfirm(false)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <ConfirmTitle>Stripe Dashboard</ConfirmTitle>
            <ConfirmText>Weet je zeker dat je naar het Stripe Dashboard wilt gaan?</ConfirmText>
            <ConfirmButtons>
              <ConfirmBtn onClick={() => setShowStripeConfirm(false)}>Annuleren</ConfirmBtn>
              <ConfirmBtn $primary onClick={() => { setShowStripeConfirm(false); window.open('https://dashboard.stripe.com/', '_blank', 'noopener,noreferrer'); }}>Ja, ga naar Stripe</ConfirmBtn>
            </ConfirmButtons>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
      {showWebsiteConfirm && (
        <ConfirmOverlay onClick={() => setShowWebsiteConfirm(false)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <ConfirmTitle>Bekijk de website</ConfirmTitle>
            <ConfirmText>Weet je zeker dat je naar KMFCshoponline.nl wilt gaan?</ConfirmText>
            <ConfirmButtons>
              <ConfirmBtn onClick={() => setShowWebsiteConfirm(false)}>Annuleren</ConfirmBtn>
              <ConfirmBtn $primary onClick={() => { setShowWebsiteConfirm(false); window.open('https://kmfcshoponline.nl', '_blank', 'noopener,noreferrer'); }}>Ja, open website</ConfirmBtn>
            </ConfirmButtons>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </MenuContext.Provider>
  );
}